/**
 * TLDraw Runtime Loader
 *
 * Centralized module for lazy-loading TLDraw. This ensures the ~150KB TLDraw
 * package is loaded only when whiteboard functionality is actually needed,
 * rather than at application startup.
 *
 * Usage:
 *   // In async contexts (init methods, sagas)
 *   await loadTldraw();
 *
 *   // After loading, use synchronously
 *   const { createTLStore, defaultShapeUtils } = getTldrawSync();
 */

export type TldrawModule = typeof import('@digitalsamba/tldraw');

let cachedModule: TldrawModule | null = null;
let loadingPromise: Promise<TldrawModule> | null = null;

/**
 * Dynamically imports TLDraw with webpack magic comments for optimal chunking.
 * - webpackChunkName: Creates a named "tldraw" chunk for easier debugging
 * - webpackPrefetch: Hints browser to prefetch after main bundle loads
 */
export const loadTldraw = async (): Promise<TldrawModule> => {
  if (cachedModule) {
    return cachedModule;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = import(
    /* webpackChunkName: "whiteboard" */
    /* webpackPrefetch: true */
    '@digitalsamba/tldraw'
  ).then((module) => {
    cachedModule = module;
    loadingPromise = null;
    return module;
  });

  return loadingPromise;
};

export const getTldrawSync = (): TldrawModule => {
  if (!cachedModule) {
    throw new Error(
      'TLDraw module not loaded. Call and await loadTldraw() before using getTldrawSync().'
    );
  }
  return cachedModule;
};

export const isTldrawLoaded = (): boolean => cachedModule !== null;
